import { ArrowTopRightOnSquareIcon, CubeIcon, ShoppingBagIcon, TagIcon } from "@heroicons/react/24/outline";

import { IPart, PartStatus } from "../../models/Part";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import Tooltip from "../shared/Tooltip";

export default function StatusIcons({ part }: { part: IPart }) {
  const offer = part.offers?.[0];
  const ebay = (() => {
    if (part.soldDate) return { label: "Sold", color: "bg-emerald-100 text-emerald-600" };
    if (part.listingProcessing) return { label: "Listing in progress", color: "bg-purple-100 text-purple-600" };
    if (offer?.publishedOn && offer?.listingStarted && new Date(offer.listingStarted) < new Date()) return { label: "Active on eBay", color: "bg-blue-100 text-blue-600" };
    if (offer?.listingEnded && new Date(offer.listingEnded) < new Date()) return { label: "Ended - not sold", color: "bg-rose-100 text-rose-600" };
    if (offer?.publishedOn) return { label: "Published", color: "bg-amber-100 text-amber-600" };
    return { label: "Not published", color: "bg-slate-100 text-slate-400" };
  })();
  const order = (() => {
    if (part.shippedOn) return { label: "Shipped", color: "bg-emerald-100 text-emerald-600" };
    if (part.pickedOn) return { label: "Picked", color: "bg-blue-100 text-blue-600" };
    if (part.soldDate) return { label: "Awaiting pick", color: "bg-amber-100 text-amber-600" };
    return { label: "No order", color: "bg-slate-100 text-slate-400" };
  })();
  const partStatusValue = part.partStatus as PartStatus | null | undefined;
  const partInfo = (() => {
    if (partStatusValue === null || partStatusValue === undefined) return { label: "Status N/A", color: "bg-slate-100 text-slate-400" };
    const label = PartStatus[partStatusValue] ?? "Unknown";
    if (partStatusValue === PartStatus.Dismantled) return { label, color: "bg-emerald-100 text-emerald-600" };
    if (partStatusValue === PartStatus["Pending Dismantle"]) return { label, color: "bg-amber-100 text-amber-600" };
    if (partStatusValue === PartStatus.QC) return { label, color: "bg-indigo-100 text-indigo-600" };
    if ([PartStatus.Damaged, PartStatus.Missing, PartStatus.Returned, PartStatus.Deleted].includes(partStatusValue)) return { label, color: "bg-rose-100 text-rose-600" };
    if (partStatusValue === PartStatus.Sold) return { label, color: "bg-blue-100 text-blue-600" };
    return { label, color: "bg-slate-100 text-slate-500" };
  })();

  const Pill = ({ color, label, Icon }: { color: string; label: string; Icon: typeof TagIcon }) => (
    <Tooltip content={label}>
      <span className={classNames("inline-flex h-7 w-7 items-center justify-center rounded-full", color)}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
    </Tooltip>
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Pill color={ebay.color} label={`eBay: ${ebay.label}`} Icon={TagIcon} />
      <Pill color={order.color} label={`Order: ${order.label}`} Icon={ShoppingBagIcon} />
      <Pill color={partInfo.color} label={`Part: ${partInfo.label}`} Icon={CubeIcon} />
      {part.ebayItemNumber && (
        <Tooltip content="View listing on eBay">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openInNewTab(`https://www.ebay.co.uk/itm/${part.ebayItemNumber}`);
            }}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-primary-600"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
